import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useCreateAgentGroup, useUpdateAgentGroup } from "@/hooks/agent";
import { AgentGroup } from "@/types/agent/agent-group";

interface GroupManagementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  group?: AgentGroup | null;
}

export const GroupManagementDialog = ({
  isOpen,
  onClose,
  group,
}: GroupManagementDialogProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const createMutation = useCreateAgentGroup();
  const updateMutation = useUpdateAgentGroup();

  useEffect(() => {
    if (isOpen) {
      setName(group?.name || "");
      setDescription(group?.description || "");
    }
  }, [isOpen, group]);

  const handleSave = async () => {
    if (!name.trim()) return;

    if (group) {
      await updateMutation.mutateAsync({
        groupId: group.id,
        name: name.trim(),
        description: description.trim() || undefined,
      });
    } else {
      await createMutation.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
      });
    }

    handleClose();
  };

  const handleClose = () => {
    onClose();
    setName("");
    setDescription("");
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent
        side="right"
        showClose={false}
        className="w-full sm:max-w-xl flex flex-col h-full p-0"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b p-6 sm:p-8">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {group ? "Edit Group" : "Create New Group"}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {group
                ? "Update the group name and description"
                : "Create a new group to organize your genies"}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-light transition-colors"
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6 space-y-6">
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-2 block">
              Group Name *
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Customer Support, Sales, etc."
              maxLength={50}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-genie-primary bg-white text-base"
            />
            {name && (
              <p className="text-xs text-gray-500 mt-1">{name.length}/50</p>
            )}
          </div>

          <div>
            <label className="text-sm font-semibold text-gray-700 mb-2 block">
              Description (optional)
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the purpose of this group..."
              maxLength={200}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-genie-primary bg-white text-base resize-none"
            />
            {description && (
              <p className="text-xs text-gray-500 mt-1">
                {description.length}/200
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 border-t px-6 sm:px-8 py-4 flex justify-start gap-3 bg-white">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name.trim() || isLoading}
            className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-lg"
          >
            {isLoading ? "Saving..." : "Save Group"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
